import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import SavedContactCombobox from '@/components/SavedContactCombobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Users,
  Truck,
  ListChecks,
  AlertCircle,
  Plus,
  Trash2,
  FileText,
  Save,
  Loader2,
  RotateCcw
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { todayEasternStr } from '@/utils/aarValidation';

const AddWorkSchedulePage = () => {
  const navigate = useNavigate();
  const { api, currentFacility } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // PROMPT 3: Reschedule mode
  const rescheduleFromWsId = searchParams.get('rescheduleFrom');
  const rescheduleNote = searchParams.get('rescheduleNote') || '';
  const isRescheduleMode = Boolean(rescheduleFromWsId);
  const [rescheduleContext, setRescheduleContext] = useState(null);
  
  // Data states
  const [serviceRequests, setServiceRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [techniciansWithNDT, setTechniciansWithNDT] = useState({}); // Map of tech ID -> NDT methods
  const [vehicles, setVehicles] = useState([]);
  // 2026-06-05 — Dedicated Site scheduling path (idle-tech scheduling to a
  // Customers & Contacts DEDICATED_SITE row). Fully separate branch from the
  // SR-based flow; SR-specific fields are hidden while active.
  const [scheduleType, setScheduleType] = useState('SR');
  const [dedicatedSites, setDedicatedSites] = useState([]);
  const [selectedDedicatedSiteId, setSelectedDedicatedSiteId] = useState('');
  const [dedicatedFacilityId, setDedicatedFacilityId] = useState('');
  const [dedicatedFacilities, setDedicatedFacilities] = useState([]);
  
  // Form states
  const [selectedSR, setSelectedSR] = useState('');
  const [srDetails, setSrDetails] = useState(null);
  const [formData, setFormData] = useState({
    schedule_start_date: '',
    schedule_end_date: '',
    priority: 'MEDIUM',
    complexity: 'STANDARD',
    notes: '',
    job_lead_id: '',
    support_team_ids: [],
    vehicle_ids: [],
    vehicle_not_needed: false,
    needs_specialist_review: false
  });

  // Must come AFTER formData declaration (uses formData.job_lead_id)
  const supportTeamOptions = useMemo(
    () => technicians.filter(t => t.id !== formData.job_lead_id),
    [technicians, formData.job_lead_id]
  );
  
  // NDT crew picker color map — uses the new backend status enum.
  const ndtStatusClass = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600';
      case 'EXPIRING': return 'text-amber-600';
      case 'EXPIRED': return 'text-red-600';
      case 'SUSPENDED': return 'text-yellow-700';
      case 'REVOKED': return 'text-red-700';
      case 'PENDING':
      default: return 'text-gray-500';
    }
  };
  const formatNdtStatus = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };
  // Visible methods on a crew card:
  //   - any method with a non-PENDING status (Active / Expiring / Expired / Suspended / Revoked).
  //   - PENDING with a level (level is informational, e.g. Trainee).
  //   - hide PENDING without a level.
  const filterVisibleNdt = (methods) => (methods || []).filter(m => m.status !== 'PENDING' || (m.level && m.level.trim()));
  // Label for a single visible method.
  const ndtMethodLabel = (m) => {
    const lvl = (m.level || '').trim();
    if (m.status === 'PENDING') {
      // Level present (e.g. Trainee) — show without status label.
      return lvl ? `${m.method} (${lvl})` : m.method;
    }
    return lvl ? `${m.method} (${lvl}, ${formatNdtStatus(m.status)})` : `${m.method} (${formatNdtStatus(m.status)})`;
  };
  const NDT_EVENT_TYPES = ['Service Equipment', 'NDT', 'Qualification'];
  const showNDTStatus = srDetails && NDT_EVENT_TYPES.includes(srDetails.event_type);
  const [lineItems, setLineItems] = useState([
    { description: '', display_order: 0 }
  ]);

  const fetchSRDetails = useCallback(async (srId) => {
    try {
      const response = await api.get(`/service-requests/${srId}`);
      setSrDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch SR details:', error);
    }
  }, [api]);

  const fetchInitialData = useCallback(async () => {
    // Phase 5 — single-facility scheduling only. If Admin/Manager is in
    // "All Facilities" mode when landing here (via direct URL, bookmark or
    // stale nav), do NOT fire the SR/crew/vehicle loaders — the backend
    // scoping would either return cross-facility data (bad UX) or 400.
    // A dedicated inline blocker in the render tree instructs the user to
    // select a specific facility from the top-left switcher.
    if (!currentFacility || currentFacility === 'all') {
      setLoading(false);
      setServiceRequests([]);
      setTechnicians([]);
      setTechniciansWithNDT({});
      setVehicles([]);
      return;
    }

    setLoading(true);
    try {
      const params = { facility_id: currentFacility };

      // Fetch eligible service requests (pre-scheduling statuses: NEW, ESTIMATE, APPROVAL, PLANNING)
      // Note: SR_SCHEDULED and CANCELLED SRs are NOT eligible for scheduling
      const srResponse = await api.get('/service-requests', { params });
      // Filter client-side to include only pre-scheduling statuses
      const eligibleStatuses = ['NEW', 'ESTIMATE', 'APPROVAL', 'PLANNING'];
      const eligibleSRs = srResponse.data.filter(sr => eligibleStatuses.includes(sr.status));
      setServiceRequests(eligibleSRs);

      // Fetch technicians for the current scheduling facility.
      // 2026-07-19 Phase 1 — pass `facility_id` so the crew picker only
      // returns techs assigned to THIS facility. Backend enforces scope.
      const techResponse = await api.get('/users', {
        params: { role: 'TECHNICIAN', facility_id: currentFacility },
      });
      setTechnicians(techResponse.data);

      // Fetch technicians with NDT status for crew picker
      try {
        const ndtResponse = await api.get('/users/technicians-with-ndt', { params });
        // Convert to map for quick lookup
        const ndtMap = {};
        ndtResponse.data.forEach(tech => {
          ndtMap[tech.id] = tech.ndt_methods || [];
        });
        setTechniciansWithNDT(ndtMap);
      } catch (error) {
        console.error('Failed to fetch NDT status:', error);
        // Non-blocking - just won't show NDT info
      }

      // Fetch vehicles
      const vehicleResponse = await api.get('/vehicles', { params });
      setVehicles(vehicleResponse.data.filter(v => v.status === 'ACTIVE'));

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, currentFacility]);

  const fetchRescheduleContext = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/work-schedules/${rescheduleFromWsId}/reschedule-context`);
      const ctx = response.data;
      setRescheduleContext(ctx);
      
      // FINAL PROMPT: MINIMAL pre-population - only SR, Complexity, Priority
      // Do NOT pre-populate: line items, dates, job lead, support team, etc.
      setSelectedSR(ctx.service_request.id);
      setFormData(prev => ({
        ...prev,
        priority: ctx.original_work_order.priority || 'MEDIUM',
        complexity: ctx.original_work_order.complexity || 'STANDARD',
        // Leave ALL other fields blank/default - treat as brand new work order
        job_lead_id: '',
        support_team_ids: [],
        notes: '',
        schedule_start_date: '',
        schedule_end_date: '',
        vehicle_ids: [],
        vehicle_not_needed: false
      }));
      
      // FINAL PROMPT: Do NOT pre-populate line items - leave blank
      setLineItems([{ description: '', display_order: 0 }]);
      
      // Fetch SR details
      fetchSRDetails(ctx.service_request.id);
    } catch (error) {
      console.error('Failed to fetch reschedule context:', error);
      toast.error('Failed to load reschedule context');
    } finally {
      setLoading(false);
    }
  }, [api, rescheduleFromWsId, fetchSRDetails]);

  // PROMPT 3: Fetch reschedule context if in reschedule mode
  useEffect(() => {
    if (isRescheduleMode && rescheduleFromWsId) {
      fetchRescheduleContext();
    }
  }, [isRescheduleMode, rescheduleFromWsId, fetchRescheduleContext]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // 2026-06-05 — Preload the Dedicated Site option lists when the user
  // switches into that mode. Uses the existing /repair-sites endpoint filtered
  // by contact_type.
  useEffect(() => {
    if (scheduleType !== 'DEDICATED_SITE') return;
    (async () => {
      try {
        const [sitesRes, facRes] = await Promise.all([
          api.get('/repair-sites'),
          api.get('/facilities'),
        ]);
        const rawSites = (sitesRes.data || []).filter((s) => s.contact_type === 'DEDICATED_SITE');
        setDedicatedSites(rawSites);
        setDedicatedFacilities(facRes.data || []);
      } catch (err) {
        toast.error('Failed to load Dedicated Site options');
      }
    })();
  }, [scheduleType, api]);

  useEffect(() => {
    if (selectedSR && !isRescheduleMode) {
      fetchSRDetails(selectedSR);
    }
  }, [selectedSR, isRescheduleMode, fetchSRDetails]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSupportTeamToggle = (techId) => {
    setFormData(prev => {
      const current = prev.support_team_ids || [];
      if (current.includes(techId)) {
        return { ...prev, support_team_ids: current.filter(id => id !== techId) };
      } else {
        return { ...prev, support_team_ids: [...current, techId] };
      }
    });
  };

  const handleVehicleToggle = (vehicleId) => {
    setFormData(prev => {
      const current = prev.vehicle_ids || [];
      const isCurrentlySelected = current.includes(vehicleId);
      
      // Create new array only if actually changing
      const newIds = isCurrentlySelected
        ? current.filter(id => id !== vehicleId)
        : [...current, vehicleId];
      
      // Safety check: only update if actually different
      if (newIds.length === current.length && isCurrentlySelected === current.includes(vehicleId)) {
        return prev; // No change needed
      }
      
      return { ...prev, vehicle_ids: newIds };
    });
  };

  const handleLineItemChange = (index, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], description: value };
      return updated;
    });
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { description: '', display_order: prev.length }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isDedicated = scheduleType === 'DEDICATED_SITE';

    if (isDedicated) {
      if (!selectedDedicatedSiteId) { toast.error('Please select a Dedicated Site'); return; }
      if (!dedicatedFacilityId) { toast.error('Please select a Facility'); return; }
      if (!formData.schedule_start_date) { toast.error('Please select a schedule start date'); return; }
      if (!formData.schedule_end_date) { toast.error('Please select a schedule end date'); return; }
      if (!formData.job_lead_id) { toast.error('Please select a job lead'); return; }
    } else {
      if (!selectedSR) { toast.error('Please select a service request'); return; }
      if (!formData.schedule_start_date) { toast.error('Please select a schedule start date'); return; }
      if (!formData.job_lead_id) { toast.error('Please select a job lead'); return; }
      if (!formData.complexity) { toast.error('Please select job complexity'); return; }
      // LINE ITEM GUARD: Require at least 1 line item during scheduling
      const validLineItems = lineItems.filter(li => li.description.trim());
      if (validLineItems.length === 0) {
        toast.error('Add at least one line item before scheduling this work order.');
        return;
      }
    }

    // 2026-07-05 Wave 2 #3: Truck validation applies to BOTH schedule types.
    // Must have at least one vehicle_id OR explicitly check "Vehicle not needed".
    const hasVehicle = (formData.vehicle_ids || []).length > 0;
    if (!hasVehicle && !formData.vehicle_not_needed) {
      toast.error('Select a truck or check "Vehicle not needed" to continue.');
      return;
    }

    setSubmitting(true);
    try {
      const validLineItems = lineItems.filter(li => li.description.trim());
      const payload = isDedicated ? {
        schedule_type: 'DEDICATED_SITE',
        dedicated_site_id: selectedDedicatedSiteId,
        facility_id: dedicatedFacilityId,
        schedule_start_date: formData.schedule_start_date,
        schedule_end_date: formData.schedule_end_date || formData.schedule_start_date,
        notes: formData.notes || null,
        job_lead_id: formData.job_lead_id,
        support_team_ids: formData.support_team_ids,
        vehicle_ids: formData.vehicle_not_needed ? [] : formData.vehicle_ids,
        vehicle_not_needed: formData.vehicle_not_needed,
      } : {
        schedule_type: 'SR',
        service_request_id: selectedSR,
        schedule_start_date: formData.schedule_start_date,
        schedule_end_date: formData.schedule_end_date || formData.schedule_start_date,
        priority: formData.priority,
        complexity: formData.complexity,
        notes: formData.notes || null,
        job_lead_id: formData.job_lead_id,
        support_team_ids: formData.support_team_ids,
        vehicle_ids: formData.vehicle_not_needed ? [] : formData.vehicle_ids,
        vehicle_not_needed: formData.vehicle_not_needed,
        needs_specialist_review: formData.needs_specialist_review,
        line_items: validLineItems.map((li, idx) => ({
          description: li.description.trim(),
          display_order: idx
        }))
      };
      
      let response;
      if (isRescheduleMode && rescheduleFromWsId) {
        // FINAL RESCHEDULE FIX: Update existing WO instead of creating new
        // Remove fields not needed for apply-reschedule endpoint
        delete payload.service_request_id;
        response = await api.put(`/work-schedules/${rescheduleFromWsId}/apply-reschedule`, payload);
        toast.success('Work schedule rescheduled successfully!');
      } else {
        response = await api.post('/work-schedules', payload);
        toast.success('Work schedule created successfully');
      }
      
      navigate('/work-schedules');
    } catch (error) {
      console.error('Failed to save work schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to save work schedule');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]"></div>
      </div>
    );
  }

  // Phase 5 — single-facility scheduling only. If the user reaches this
  // route while the top-left selector is still on "All Facilities" /
  // "All My Facilities" show a clean inline blocker instead of a broken
  // form. Loaders are also short-circuited above.
  if (!currentFacility || currentFacility === 'all') {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="add-ws-facility-blocked">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/work-schedules')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#323033]">Add Work Schedule</h1>
            <p className="text-sm text-[#5A5A5A]">Schedule work for a service request</p>
          </div>
        </div>

        <Card>
          <CardContent className="py-10 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-[#FEF3C7] flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-[#B45309]" />
            </div>
            <h2 className="text-lg font-semibold text-[#323033] mb-2">
              Select a specific facility to continue
            </h2>
            <p className="text-sm text-[#5A5A5A] max-w-md mb-4">
              Work schedules are single-facility. Use the facility selector in
              the top-left to choose a specific facility, then return to this page.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/work-schedules')}
              data-testid="add-ws-return-btn"
            >
              Back to Work Schedules
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="add-work-schedule-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/work-schedules')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">
            {isRescheduleMode ? 'Reschedule Work Order' : 'Add Work Schedule'}
          </h1>
          {!isRescheduleMode && (
            <p className="text-[#5A5A5A] mt-1">Schedule work for a service request</p>
          )}
        </div>
      </div>

      {/* Reschedule mode banner - Minimal: just heading and reason */}
      {isRescheduleMode && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <RotateCcw className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                {rescheduleNote && (
                  <p className="text-sm text-amber-700">
                    <span className="font-medium">Reason:</span> {rescheduleNote}
                  </p>
                )}
                {!rescheduleNote && (
                  <p className="text-sm text-amber-700">Fill in the new scheduling details below</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 2026-06-05 — Schedule mode selector. Hidden when we're in
            reschedule mode (SR is locked to the original work order). */}
        {!isRescheduleMode && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <span className="text-sm font-medium text-[#323033]">Schedule Type:</span>
                <div className="inline-flex items-center gap-2" data-testid="schedule-type-toggle">
                  <Button
                    type="button"
                    variant={scheduleType === 'SR' ? 'default' : 'outline'}
                    size="sm"
                    className={scheduleType === 'SR' ? 'bg-[#005EF8] hover:bg-[#0047C2] text-white' : ''}
                    onClick={() => setScheduleType('SR')}
                    data-testid="schedule-type-sr"
                  >
                    Service Request
                  </Button>
                  <Button
                    type="button"
                    variant={scheduleType === 'DEDICATED_SITE' ? 'default' : 'outline'}
                    size="sm"
                    className={scheduleType === 'DEDICATED_SITE' ? 'bg-[#005EF8] hover:bg-[#0047C2] text-white' : ''}
                    onClick={() => setScheduleType('DEDICATED_SITE')}
                    data-testid="schedule-type-dedicated"
                  >
                    Dedicated Site
                  </Button>
                </div>
                <span className="text-xs text-[#5A5A5A]">
                  {scheduleType === 'DEDICATED_SITE'
                    ? 'For idle-tech scheduling to a Dedicated Site — no SR / car / event type required.'
                    : 'Schedules against an existing Service Request.'}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dedicated Site picker (only when in DEDICATED_SITE mode). */}
        {scheduleType === 'DEDICATED_SITE' && !isRescheduleMode && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#005EF8]" />
                Dedicated Site
              </CardTitle>
              <CardDescription>Pick a Dedicated Site and target facility.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Dedicated Site *</Label>
                <Select value={selectedDedicatedSiteId} onValueChange={setSelectedDedicatedSiteId}>
                  <SelectTrigger data-testid="dedicated-site-select">
                    <SelectValue placeholder={dedicatedSites.length ? 'Select a dedicated site' : 'No dedicated sites — create one in Customers & Contacts'} />
                  </SelectTrigger>
                  <SelectContent>
                    {dedicatedSites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.repair_site_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Facility *</Label>
                <Select value={dedicatedFacilityId} onValueChange={setDedicatedFacilityId}>
                  <SelectTrigger data-testid="dedicated-facility-select">
                    <SelectValue placeholder="Select facility" />
                  </SelectTrigger>
                  <SelectContent>
                    {dedicatedFacilities.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Select Service Request (SR mode only) */}
        {scheduleType === 'SR' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#005EF8]" />
              {isRescheduleMode ? 'Service Request' : 'Select Service Request'}
            </CardTitle>
            <CardDescription>
              {isRescheduleMode 
                ? 'Pre-selected from the original work order'
                : 'Choose an existing service request to schedule'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="service_request">Service Request *</Label>
              <SavedContactCombobox
                triggerId="service_request"
                testId="sr-select"
                options={serviceRequests}
                primary={(sr) => `${sr.car_mark || ''} ${sr.car_number || ''}`.trim() || sr.job_number}
                secondary={(sr) => `${sr.job_number}${sr.site_name ? ` • ${sr.site_name}` : ''}${sr.event_type ? ` • ${sr.event_type}` : ''}`}
                selectedId={selectedSR}
                onSelect={(id) => setSelectedSR(id)}
                placeholder="Select a service request..."
                emptyMessage={isRescheduleMode ? 'Service Request locked' : 'No eligible service requests'}
                searchPlaceholder="Search by SR number, car, requestor…"
                disabled={isRescheduleMode}
              />
              {serviceRequests.length === 0 && !isRescheduleMode && (
                <p className="text-sm text-orange-600 mt-2">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  No &quot;NEW&quot; service requests available. Create one first.
                </p>
              )}
            </div>

            {/* SR Details Preview */}
            {srDetails && (
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <h4 className="font-medium text-[#323033]">Selected Request Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-[#5A5A5A]">Car:</span>
                    <p className="font-medium">{srDetails.car_mark} {srDetails.car_number}</p>
                  </div>
                  <div>
                    <span className="text-[#5A5A5A]">Owner:</span>
                    <p className="font-medium">{srDetails.car_owner}</p>
                  </div>
                  <div>
                    <span className="text-[#5A5A5A]">Site:</span>
                    <p className="font-medium">{srDetails.site_name}</p>
                  </div>
                  <div>
                    <span className="text-[#5A5A5A]">Event Type:</span>
                    <p className="font-medium">{srDetails.event_type}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Step 2: Schedule Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#005EF8]" />
              Schedule Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="schedule_start_date">Schedule Start Date *</Label>
                <Input
                  type="date"
                  id="schedule_start_date"
                  name="schedule_start_date"
                  value={formData.schedule_start_date}
                  onChange={handleInputChange}
                  /* 2026-07-19 — Anchor "today" to US Eastern (same rule as
                     AAR line-item date gate). Prior code used
                     `new Date().toISOString().split('T')[0]` which is UTC —
                     that rolled the min value forward one day in the
                     evening hours west of UTC, disabling today for users. */
                  min={todayEasternStr()}
                  required
                  data-testid="schedule-start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="schedule_end_date">Schedule End Date</Label>
                <Input
                  type="date"
                  id="schedule_end_date"
                  name="schedule_end_date"
                  value={formData.schedule_end_date}
                  onChange={handleInputChange}
                  min={formData.schedule_start_date || todayEasternStr()}
                  data-testid="schedule-end-date-input"
                />
                <p className="text-xs text-[#5A5A5A] mt-1">Leave blank for single-day jobs</p>
              </div>
              {/* 2026-07-05 Wave 2 #2: Priority + Complexity removed on Dedicated Site path. */}
              {scheduleType !== 'DEDICATED_SITE' && (
                <>
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => handleSelectChange('priority', v)}
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOW">Low</SelectItem>
                        <SelectItem value="MEDIUM">Medium</SelectItem>
                        <SelectItem value="HIGH">High</SelectItem>
                        <SelectItem value="CRITICAL">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="complexity">Complexity *</Label>
                    <Select
                      value={formData.complexity || ''}
                      onValueChange={(v) => handleSelectChange('complexity', v)}
                    >
                      <SelectTrigger data-testid="complexity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LIGHT">Light</SelectItem>
                        <SelectItem value="STANDARD">Standard</SelectItem>
                        <SelectItem value="HEAVY">Heavy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            {/* 2026-07-05 Wave 2 #2: Specialist Review removed on Dedicated Site path. */}
            {scheduleType !== 'DEDICATED_SITE' && (
              <div className="flex items-center space-x-3 py-2">
                <Checkbox
                  id="needs_specialist_review"
                  checked={formData.needs_specialist_review}
                  onCheckedChange={(checked) => handleSelectChange('needs_specialist_review', checked)}
                  data-testid="needs-specialist-review-checkbox"
                />
                <Label
                  htmlFor="needs_specialist_review"
                  className="text-sm font-medium cursor-pointer flex items-center gap-2"
                >
                  Needs specialist review
                  <span className="text-xs text-[#808080] font-normal">
                    (flags this schedule for special attention)
                  </span>
                </Label>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes about this work schedule..."
                rows={3}
                data-testid="notes-textarea"
              />
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Crew Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#005EF8]" />
              Crew Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="job_lead">Job Lead *</Label>
              <Select value={formData.job_lead_id} onValueChange={(v) => handleSelectChange('job_lead_id', v)}>
                <SelectTrigger data-testid="job-lead-select">
                  <SelectValue placeholder="Select job lead..." />
                </SelectTrigger>
                <SelectContent>
                  {technicians.map((tech) => {
                    const ndtMethods = filterVisibleNdt(techniciansWithNDT[tech.id]);
                    return (
                      <SelectItem key={tech.id} value={tech.id}>
                        <div className="flex flex-col">
                          <span>
                            {tech.first_name} {tech.last_name} {tech.technician_id ? `(${tech.technician_id})` : ''}
                          </span>
                          {showNDTStatus && ndtMethods.length > 0 && (
                            <span className="text-xs text-gray-500">
                              NDT: {ndtMethods.map((m, idx) => (
                                <span key={m.method}>
                                  {idx > 0 && ', '}
                                  <span className={ndtStatusClass(m.status)}>
                                    {ndtMethodLabel(m)}
                                  </span>
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Support Team (Optional)</Label>
              <p className="text-sm text-[#5A5A5A] mb-2">Select additional technicians to assist</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {supportTeamOptions.map((tech) => {
                    const ndtMethods = filterVisibleNdt(techniciansWithNDT[tech.id]);
                    return (
                      <div key={tech.id} className="flex items-start space-x-2 p-2 border rounded hover:bg-gray-50">
                        <Checkbox
                          id={`support-${tech.id}`}
                          checked={(formData.support_team_ids || []).includes(tech.id)}
                          onCheckedChange={() => handleSupportTeamToggle(tech.id)}
                          data-testid={`support-tech-${tech.id}`}
                          className="mt-1"
                        />
                        <label
                          htmlFor={`support-${tech.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          <div className="font-medium">{tech.first_name} {tech.last_name}</div>
                          {showNDTStatus && ndtMethods.length > 0 && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              NDT: {ndtMethods.map((m, idx) => (
                                <span key={m.method}>
                                  {idx > 0 && ', '}
                                  <span className={ndtStatusClass(m.status)}>
                                    {ndtMethodLabel(m)}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </label>
                      </div>
                    );
                  })}
              </div>
              {technicians.length <= 1 && (
                <p className="text-sm text-[#5A5A5A] mt-2">No additional technicians available.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step 4: Vehicle Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#005EF8]" />
              Vehicle Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vehicle_not_needed"
                checked={formData.vehicle_not_needed}
                onCheckedChange={(checked) => setFormData(prev => ({ 
                  ...prev, 
                  vehicle_not_needed: checked,
                  vehicle_ids: checked ? [] : prev.vehicle_ids
                }))}
                data-testid="vehicle-not-needed"
              />
              <label htmlFor="vehicle_not_needed" className="text-sm cursor-pointer">
                Vehicle not needed for this job
              </label>
            </div>

            {!formData.vehicle_not_needed && (
              <div>
                <Label>Select Vehicles</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                  {vehicles.length === 0 ? (
                    <p className="text-sm text-[#5A5A5A] col-span-full">
                      No active vehicles available. <button type="button" onClick={() => navigate('/vehicles')} className="text-[#005EF8] hover:underline">Add vehicles</button>
                    </p>
                  ) : (
                    vehicles.map((vehicle) => {
                      const isSelected = (formData.vehicle_ids || []).includes(vehicle.id);
                      return (
                        <div
                          key={vehicle.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-[#005EF8] bg-[#005EF8]/5'
                              : 'border-border hover:border-[#005EF8]/50'
                          }`}
                          onClick={() => handleVehicleToggle(vehicle.id)}
                          data-testid={`vehicle-${vehicle.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div 
                              className={`w-4 h-4 rounded border flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-[#005EF8] border-[#005EF8]' 
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{vehicle.display_id}</p>
                              <p className="text-xs text-[#5A5A5A]">
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 5: Line Items — SR mode only (N/A for Dedicated Site). */}
        {scheduleType === 'SR' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-[#005EF8]" />
              Line Items
            </CardTitle>
            <CardDescription>
              Add specific tasks to track for this job. Technicians must complete all items before the job can be marked complete.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lineItems.map((item, index) => (
              <div key={`item-${index}`} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    placeholder={`Line item ${index + 1} description...`}
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, e.target.value)}
                    data-testid={`line-item-${index}`}
                  />
                </div>
                {lineItems.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLineItem(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addLineItem}
              className="w-full"
              data-testid="add-line-item-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Line Item
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/work-schedules')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            disabled={submitting || (scheduleType === 'SR' ? !selectedSR : !selectedDedicatedSiteId)}
            data-testid="submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isRescheduleMode ? 'Rescheduling...' : 'Creating...'}
              </>
            ) : (
              <>
                {isRescheduleMode ? <RotateCcw className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {isRescheduleMode ? 'Reschedule Work Order' : 'Create Work Schedule'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddWorkSchedulePage;
